import type { Config } from 'dompurify'

import isObject from 'lodash-es/isObject'
import { Directive, DirectiveBinding, DirectiveHook } from 'vue'

import { sanitizeHtml } from '/@/utils/lib/sanitize-html'

export const DEFAULT_CONFIG: Config = {
  ALLOWED_TAGS: [
    'i',
    'h1',
    'h2',
    'h3',
    'p',
    'span',
    'b',
    'strong',
    'code',
    'pre',
    'u',
    's',
    'li',
    'ul',
    'ol',
    'a',
    'img',
    'figcaption',
    'figure',
    'br',
    'hr',
    'div',
    'quote',
    'blockquote',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'mark',
    'details',
    'summary',
  ],
  ALLOWED_ATTR: [
    'href',
    'rel',
    'target',
    'class',
    'src',
    'data-oembed-url',
    'style',
    'width',
    'height',
    'colspan',
    'rowspan',
  ],
}

/**
 * Updates the component with the sanitized value
 *
 * @var html
 * const html = `<img src="simple xss" onerror="alert(document.cookie.split(';'))"><i>selam</i>`
 *
 * @example
 * <div v-sanitize="html"></div>
 * => <i>selam</i>
 *
 * @example
 * <div v-sanitize="{ html, options: { ALLOWED_TAGS: ['i', 'img'] } }"></div>
 * => <i>selam</i><img src="simple xss" onerror="alert(document.cookie.split(';'))">
 *
 * @param el
 * @param binding
 */
const updateComponent: DirectiveHook = (
  el: HTMLElement,
  binding: DirectiveBinding<string | { html: string; options: Config }>,
) => {
  // Hold the current value of the binding
  let currentValue
  // Keeps default settings by default
  let config: Config = DEFAULT_CONFIG

  // If the binding value is an object, we use the html property as the value. We also use the options property as the config.
  // Otherwise, we use the binding value as the value.
  if (isObject(binding.value)) {
    // Set the current value to the html property of the binding value
    currentValue = binding.value.html
    // Merge the default config with the options property of the binding value
    config = { ...DEFAULT_CONFIG, ...binding.value.options }
  } else currentValue = binding.value

  // If the current value is the same as the old value, we don't need to do anything
  if (binding.oldValue === currentValue) return

  // Set the innerHTML of the element to the sanitized value
  el.innerHTML = sanitizeHtml(currentValue as string, config) as string
}

export const vSanitize: Directive = {
  mounted: updateComponent,
  updated: updateComponent,
}
